import { Chip, type ChipProps } from "@mui/material";
import { getJobStatusChipStyle, type JobStatusChipStyle } from "@domain/CRM/job";

interface JobStatusChipProps {
	/** A status from either system: "1. Ready", or the new system's "Ready". */
	status: string;
	/** Outlined for a status the job is heading for rather than at. */
	variant?: ChipProps["variant"];
	/** Set in `micro`, the size of the compact flag chips and the age badge, for job cards. */
	compact?: boolean;
	sx?: ChipProps["sx"];
}

// A status can be longer than a narrow card or column. Let the label wrap and the chip grow,
// so it is never clipped with an ellipsis.
const WRAPPING_SX = {
	height: "auto",
	minHeight: 24,
	"& .MuiChip-label": { whiteSpace: "normal", py: 0.25 },
} as const;

const COMPACT_SX = { typography: "micro", minHeight: 0, "& .MuiChip-label": { py: 0.125 } } as const;

/**
 * The two lighter steps of a hue's ramp, as tints of the theme color over whatever the chip
 * sits on. Tinting the palette channel rather than naming a lighter hex keeps both readable in
 * either color scheme with the page's own text color. The full step needs nothing here: it is
 * MUI's filled chip, which picks its own contrast text.
 */
function toneSx({ hue, tone }: JobStatusChipStyle) {
	if (hue === "default" || tone === "full") return null;
	const channel = `var(--mui-palette-${hue}-mainChannel)`;
	return {
		bgcolor: `rgba(${channel} / ${tone === "soft" ? 0.16 : 0.45})`,
		color: "text.primary",
		// An inset ring rather than a border: a border would make these chips 2px taller than
		// the fully filled ones beside them.
		boxShadow: `inset 0 0 0 1px rgba(${channel} / ${tone === "soft" ? 0.5 : 0.7})`,
	};
}

/**
 * The one way the portal shows a job's status: a chip in the hue of its phase, stronger the
 * further along that phase it is (see `getJobStatusChipStyle`).
 */
export function JobStatusChip({ status, variant = "filled", compact = false, sx }: JobStatusChipProps) {
	const style = getJobStatusChipStyle(status);
	// An outlined chip is a status not reached yet: the hue alone, without the tone's fill.
	const tone = variant === "filled" ? toneSx(style) : null;
	return (
		<Chip
			size="small"
			label={status}
			color={style.hue}
			variant={variant}
			sx={[WRAPPING_SX, compact && COMPACT_SX, tone, ...(Array.isArray(sx) ? sx : [sx])]}
		/>
	);
}
